/**
 * Wishlist Functionality
 * Handles adding/removing products from wishlist using localStorage
 */

(function() {
  'use strict';

  const WISHLIST_STORAGE_KEY = 'shopify_wishlist';
  const WISHLIST_EVENT_ADD = 'wishlist:add';
  const WISHLIST_EVENT_REMOVE = 'wishlist:remove';
  const WISHLIST_EVENT_UPDATE = 'wishlist:update';

  class Wishlist {
    constructor() {
      this.items = this.loadWishlist();
      this.init();
    }

    init() {
      // Listen for wishlist button clicks
      document.addEventListener('click', this.handleWishlistClick.bind(this));
      
      // Update wishlist count on page load
      this.updateWishlistCount();
      
      // Listen for custom events
      document.addEventListener(WISHLIST_EVENT_ADD, () => this.updateWishlistCount());
      document.addEventListener(WISHLIST_EVENT_REMOVE, () => this.updateWishlistCount());
    }

    loadWishlist() {
      try {
        const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error loading wishlist:', e);
        return [];
      }
    }

    saveWishlist() {
      try {
        localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(this.items));
        document.dispatchEvent(new CustomEvent(WISHLIST_EVENT_UPDATE));
      } catch (e) {
        console.error('Error saving wishlist:', e);
      }
    }

    add(productId, variantId = null, productData = {}) {
      const item = {
        id: productId,
        variant_id: variantId || productData.variant_id || null,
        added_at: new Date().toISOString(),
        ...productData
      };

      // Check if already in wishlist
      if (this.isInWishlist(productId, variantId)) {
        return false;
      }

      this.items.push(item);
      this.saveWishlist();
      document.dispatchEvent(new CustomEvent(WISHLIST_EVENT_ADD, { detail: item }));
      return true;
    }

    remove(productId, variantId = null) {
      const initialLength = this.items.length;
      this.items = this.items.filter(item => {
        if (variantId) {
          return !(item.id === productId && item.variant_id === variantId);
        }
        return item.id !== productId;
      });

      if (this.items.length !== initialLength) {
        this.saveWishlist();
        document.dispatchEvent(new CustomEvent(WISHLIST_EVENT_REMOVE, { 
          detail: { id: productId, variant_id: variantId } 
        }));
        return true;
      }
      return false;
    }

    isInWishlist(productId, variantId = null) {
      return this.items.some(item => {
        if (variantId) {
          return item.id === productId && item.variant_id === variantId;
        }
        return item.id === productId;
      });
    }

    getAll() {
      return this.items;
    }

    getCount() {
      return this.items.length;
    }

    clear() {
      this.items = [];
      this.saveWishlist();
      document.dispatchEvent(new CustomEvent(WISHLIST_EVENT_REMOVE));
    }

    updateWishlistCount() {
      const count = this.getCount();
      const countElements = document.querySelectorAll('[data-wishlist-count]');
      countElements.forEach(el => {
        el.textContent = count;
        el.classList.toggle('hide', count === 0);
      });

      // Update button states
      document.querySelectorAll('[data-wishlist-button]').forEach(button => {
        const productId = button.getAttribute('data-product-id');
        const variantId = button.getAttribute('data-variant-id');
        const isInWishlist = this.isInWishlist(productId, variantId);
        
        button.classList.toggle('wishlist-button--active', isInWishlist);
        button.setAttribute('aria-pressed', isInWishlist);
        
        const iconEmpty = button.querySelector('.wishlist-button__icon-empty');
        const iconFilled = button.querySelector('.wishlist-button__icon-filled');
        if (iconEmpty && iconFilled) {
          iconEmpty.style.display = isInWishlist ? 'none' : 'block';
          iconFilled.style.display = isInWishlist ? 'block' : 'none';
        }
      });
    }

    handleWishlistClick(e) {
      const button = e.target.closest('[data-wishlist-button]');
      if (!button) return;

      e.preventDefault();
      e.stopPropagation();

      const productId = button.getAttribute('data-product-id');
      const variantId = button.getAttribute('data-variant-id') || null;
      
      if (!productId) {
        console.error('Product ID not found on wishlist button');
        return;
      }

      // Get product data from button attributes or data attributes
      const productData = {
        title: button.getAttribute('data-product-title') || '',
        handle: button.getAttribute('data-product-handle') || '',
        url: button.getAttribute('data-product-url') || '',
        image: button.getAttribute('data-product-image') || '',
        price: button.getAttribute('data-product-price') || '',
        variant_id: variantId
      };

      const isInWishlist = this.isInWishlist(productId, variantId);

      if (isInWishlist) {
        this.remove(productId, variantId);
        this.showNotification('Removed from wishlist', 'remove');
      } else {
        this.add(productId, variantId, productData);
        this.showNotification('Added to wishlist', 'add');
      }

      this.updateWishlistCount();
    }

    showNotification(message, type = 'add') {
      // Remove existing notification if any
      const existing = document.querySelector('.wishlist-notification');
      if (existing) {
        existing.remove();
      }

      const notification = document.createElement('div');
      notification.className = `wishlist-notification wishlist-notification--${type}`;
      notification.textContent = message;
      document.body.appendChild(notification);

      // Trigger animation
      requestAnimationFrame(() => {
        notification.classList.add('wishlist-notification--show');
      });

      // Remove after 3 seconds
      setTimeout(() => {
        notification.classList.remove('wishlist-notification--show');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  }

  // Initialize wishlist on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.wishlist = new Wishlist();
    });
  } else {
    window.wishlist = new Wishlist();
  }

  // Export for use in other scripts
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = Wishlist;
  }
})();
